import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

interface AlignmentSample {
  label: string;
  original_timestamp: string;
  original_value: number | string;
  aligned_timestamp: string;
  aligned_value: number;
  is_interpolated: boolean;
  match: boolean | null;
  diff_pct: number | null;
}

interface AlignmentComparisonSummary {
  original_records: number;
  aligned_records: number;
  overlap_records: number;
  interpolated_records: number;
  avg_diff_pct: number;
  completion_method: string;
}

interface AlignmentComparisonResponse {
  success: boolean;
  data_type: 'consumption' | 'price';
  unit: string;
  samples: AlignmentSample[];
  summary: AlignmentComparisonSummary;
}

export function useAlignmentComparison(
  projectId: string | undefined,
  dataType: 'consumption' | 'price',
  isAligned: boolean // Only fetch if data is aligned
) {
  return useQuery<AlignmentComparisonResponse>({
    queryKey: ['alignmentComparison', projectId, dataType],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const response = await axios.post(
        `${API_BASE_URL}/projects/${projectId}/alignment-comparison`,
        { data_type: dataType }
      );

      return response.data;
    },
    enabled: !!projectId && isAligned, // Only fetch if project exists and data is aligned
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
